<script setup lang="ts">
import { ref, onUnmounted } from 'vue'

defineProps<{
  url: string
}>()

const copied = ref(false)
let resetTimeout: number | undefined

async function copy() {
  if (typeof navigator === 'undefined' || !navigator.clipboard) {
    selectInput()
    return
  }
  try {
    await navigator.clipboard.writeText(
      (document.getElementById('widget-share-input') as HTMLInputElement)?.value ?? '',
    )
    copied.value = true
    if (resetTimeout) window.clearTimeout(resetTimeout)
    resetTimeout = window.setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch {
    selectInput()
  }
}

function selectInput() {
  const input = document.getElementById('widget-share-input') as HTMLInputElement | null
  input?.focus()
  input?.select()
}

onUnmounted(() => {
  if (resetTimeout) window.clearTimeout(resetTimeout)
})
</script>

<template>
  <div class="widget-share-url">
    <label for="widget-share-input" class="widget-share-url__label"> Del denne beregning </label>
    <p class="widget-share-url__hint">
      Linket gemmer dine valg, så modtageren ser præcis samme beregning.
    </p>
    <div class="widget-share-url__field">
      <input
        id="widget-share-input"
        class="widget-share-url__input"
        type="text"
        readonly
        :value="url"
        spellcheck="false"
        @focus="($event.target as HTMLInputElement).select()"
      />
      <button
        type="button"
        class="widget-share-url__button"
        :class="{ 'widget-share-url__button--copied': copied }"
        @click="copy"
      >
        {{ copied ? 'Kopieret' : 'Kopiér' }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.widget-share-url {
  display: flex;
  flex-direction: column;
  gap: space(2);
  padding: space(5);
  background: color('white');
  border: 1px solid color('background-medium');
  border-radius: $radius-lg;

  &__label {
    font-size: $font-size-sm;
    font-weight: $font-weight-semibold;
    color: color('primary');
  }

  &__hint {
    margin: 0;
    font-size: $font-size-xs;
    color: color('ink-light');
  }

  &__field {
    display: flex;
    align-items: stretch;
    gap: space(2);
    padding: space(1) space(1) space(1) space(3);
    background: color('background');
    border: 1px solid color('background-medium');
    border-radius: $radius-md;
    transition: border-color $duration-base $easing-standard;

    &:focus-within {
      border-color: color('accent');
    }
  }

  &__input {
    flex: 1;
    min-width: 0;
    padding: space(2) 0;
    background: transparent;
    border: none;
    outline: none;
    font-family: $font-family-mono;
    font-size: $font-size-xs;
    color: color('ink-light');
    text-overflow: ellipsis;
  }

  &__button {
    flex-shrink: 0;
    padding: space(2) space(4);
    background: color('accent');
    color: color('white');
    border: none;
    border-radius: $radius-sm;
    font-family: inherit;
    font-size: $font-size-sm;
    font-weight: $font-weight-semibold;
    cursor: pointer;
    transition:
      background $duration-base $easing-standard,
      transform $duration-fast $easing-standard;
    white-space: nowrap;

    &:hover {
      background: color('accent-dark');
    }

    &:focus-visible {
      outline: 2px solid color('accent-dark');
      outline-offset: 2px;
    }

    &--copied {
      background: color('success');
    }
  }
}
</style>
